/**
 * Un fallo de validacion, contado en castellano (NEW5-001, REAUD-003).
 *
 * QUE PROBLEMA CIERRA
 *
 * Toda la aplicacion escribia `setError(caught.message)`. Cuando lo que
 * llegaba era un `ZodError`, `message` es el JSON de sus `issues`, asi que la
 * pantalla pintaba esto, literalmente:
 *
 *   [{"origin":"number","code":"too_big","maximum":230,"path":["heightCm"], ...
 *
 * En el Lote 5 se arreglo el sitio de mas friccion —el registro de series— con
 * `validacionSerie.ts`. Quedaban los demas, y el peor de todos es `PerfilPage`:
 * es la pantalla de alta, lo primero que ve una instalacion nueva.
 *
 * LA DIFERENCIA QUE NO SE PUEDE PERDER
 *
 * Un fallo de validacion y un fallo al escribir en Dexie no son lo mismo, y
 * taparlos con la misma frase manda al usuario a corregir un numero que estaba
 * bien. Por eso lo que NO es validacion sigue enseñando su `message`: «base
 * bloqueada» es una frase util, y no es el volcado de ningun esquema.
 *
 * Y dentro de la validacion hay otra distincion, la que este repositorio ya
 * documenta en R-24: si el campo que falla NO es uno que el usuario teclee
 * —una columna de sincronizacion, un `id`, un dato que ya estaba guardado—,
 * nombrarlo manda a mirar donde no se puede hacer nada. Ahi se dice que hay un
 * dato guardado que no encaja, y el detalle tecnico va al registro.
 *
 * LOS LIMITES
 *
 * Los numeros de las frases estan escritos aqui, y `errores.test.ts` los pasa
 * por el contrato de verdad para comprobar que siguen siendo los suyos. Es el
 * mismo trato que en `validacionSerie.ts`: no se leen los internos de Zod, se
 * fija el borde con un test que falla si el contrato cambia.
 */

/** La forma de un `issue` de Zod, sin depender de la clase. */
interface ProblemaDeForma {
  code?: unknown;
  expected?: unknown;
  path?: unknown;
}

export function problemasDe(error: unknown): ProblemaDeForma[] | null {
  if (typeof error !== 'object' || error === null) return null;
  const issues = (error as { issues?: unknown }).issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  // Se comprueba la FORMA, no `instanceof ZodError`: el error puede venir del
  // Zod de `@cadencia/contracts`, que es otra instancia del modulo.
  if (!issues.every((issue) => typeof issue === 'object' && issue !== null)) return null;
  return issues as ProblemaDeForma[];
}

export function campoDe(problema: ProblemaDeForma): string | null {
  const path = problema.path;
  if (!Array.isArray(path) || path.length === 0) return null;
  return typeof path[0] === 'string' ? path[0] : null;
}

/** ¿Es un fallo de validacion, venga del esquema que venga? */
export function esErrorDeValidacion(error: unknown): boolean {
  return problemasDe(error) !== null;
}

/** Los campos que fallaron, para el registro. Nunca para la pantalla. */
export function camposConProblema(error: unknown): string[] {
  const problemas = problemasDe(error);
  if (problemas === null) return [];
  return [...new Set(problemas.map((problema) => campoDe(problema) ?? '?'))];
}

/**
 * Frases por campo.
 *
 * Completas, no plantillas: en castellano el genero y el numero cambian la
 * frase entera —«no es valida», «no son validas», «no es valido»—, asi que una
 * plantilla generica produce texto mal escrito.
 *
 * Van agrupadas por ENTIDAD y no en una lista plana porque el mismo nombre de
 * campo puede tener rangos distintos en dos contratos, y una tabla plana
 * acabaria contando un rango que no es el que rechazo el dato. Es el mismo
 * cuidado que `CLAUDE.md` pide para las unidades.
 */
const FRASES = {
  perfil: {
    displayName: 'El nombre no puede estar vacío y no puede pasar de 80 caracteres.',
    heightCm: 'La altura debe estar entre 120 y 230 cm.',
    experienceYears: 'Los años de experiencia deben estar entre 0 y 80.',
    birthDate: 'La fecha de nacimiento no es válida.',
    detrainedSince: 'La fecha de inactividad no es válida.',
    timezone: 'La zona horaria no es válida.',
  },
  peso: {
    weightKg: 'El peso debe estar entre 30 y 300 kg.',
    bodyFatPct: 'El porcentaje de grasa debe estar entre 1 y 70.',
    measuredOn: 'La fecha de la pesada no es válida.',
    note: 'La nota no puede pasar de 200 caracteres.',
  },
  pasos: {
    steps: 'Los pasos tienen que ser un número entero entre 0 y 100.000.',
    activityDate: 'La fecha de los pasos no es válida.',
  },
  medidas: {
    waistCm: 'La cintura debe estar entre 30 y 250 cm.',
    chestCm: 'El pecho debe estar entre 30 y 250 cm.',
    armCm: 'El brazo debe estar entre 10 y 100 cm.',
    thighCm: 'El muslo debe estar entre 20 y 150 cm.',
    calfCm: 'El gemelo debe estar entre 10 y 100 cm.',
    measuredOn: 'La fecha de la medición no es válida.',
    note: 'La nota no puede pasar de 200 caracteres.',
  },
  cardio: {
    // El contrato guarda SEGUNDOS (60 a 36.000) y el usuario teclea MINUTOS.
    // La frase habla en lo que se teclea: contarle segundos a quien ha escrito
    // «45» le manda a buscar un campo que no existe.
    durationSeconds: 'La duración debe estar entre 1 y 600 minutos.',
    distanceKm: 'La distancia debe ser mayor que 0 y no pasar de 999 km.',
    perceivedEffort: 'El esfuerzo percibido debe estar entre 1 y 5.',
    notes: 'Las notas no pueden pasar de 500 caracteres.',
  },
  equipo: {
    label: 'El nombre del equipo no puede estar vacío.',
    barWeightKg: 'El peso de la barra debe estar entre 0 y 60 kg.',
    minLoadKg: 'La carga mínima debe estar entre 0 y 500 kg.',
    maxLoadKg: 'La carga máxima debe estar entre 0 y 1000 kg, y no puede ser menor que la mínima.',
    incrementKg: 'El incremento debe ser mayor que 0 y no pasar de 50 kg.',
    microIncrementKg:
      'El micro-incremento debe ser mayor que 0 y no pasar de 10 kg. Si has marcado que hay micro-incrementos, hace falta indicarlo.',
    dumbbellsKg: 'Cada mancuerna debe pesar más de 0 y no pasar de 120 kg.',
  },
} as const;

/** Las entidades con campos que el usuario teclea. */
export type Entidad = keyof typeof FRASES;

/**
 * Lo que se dice cuando falla un campo de esa entidad que el usuario NO teclea.
 *
 * Nombrar `syncSeq` o `originDeviceId` no ayuda a nadie y saca a la pantalla el
 * nombre interno de un esquema, que es justo lo que este modulo existe para
 * evitar.
 */
const GENERICO_POR_ENTIDAD: Record<Entidad, string> = {
  perfil: 'Hay algún dato del perfil que no encaja. Revisa lo que has escrito.',
  cardio: 'Hay algún dato del cardio que no encaja. Revisa lo que has escrito.',
  peso: 'Hay algún dato de la pesada que no encaja. Revisa lo que has escrito.',
  pasos: 'Hay algún dato de los pasos que no encaja. Revisa lo que has escrito.',
  medidas: 'Hay alguna medida que no encaja. Revisa lo que has escrito.',
  equipo: 'Hay algún dato del equipo que no encaja. Revisa lo que has escrito.',
};

/**
 * Y cuando ni siquiera se sabe de que entidad se trata.
 *
 * Es el caso de los sitios que solo LEEN: ahi un fallo de validacion no lo ha
 * provocado nadie tecleando, lo provoca una fila guardada que no encaja con lo
 * que el contrato espera hoy. Decir «revisa lo que has escrito» seria mentir.
 */
export const MENSAJE_DATO_GUARDADO =
  'Hay un dato guardado que no encaja con lo que espera la aplicación.';

/**
 * El detalle tecnico, para el REGISTRO. Nunca para la pantalla.
 *
 * Es lo que habia antes en todas partes: si es un `ZodError`, su `message` es
 * el JSON de los `issues`. Ahi dentro esta justo lo que hace falta para
 * arreglar el fallo —que campo, que codigo, que limite— y por eso sigue
 * yendo al `logger` tal cual.
 *
 * Existe como funcion aparte a proposito. Al centralizar los mensajes casi se
 * traduce tambien lo que se registra, y eso habria dejado los diagnosticos
 * diciendo «hay un dato que no encaja» en vez del campo concreto: el fallo que
 * `CLAUDE.md` documenta como «un diagnostico que solo existe en `logger` no
 * existe en el movil», pero peor, porque ya no existiria en ningun sitio.
 */
export function detalleDeError(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

/**
 * El texto que se le enseña al usuario para un fallo cualquiera.
 *
 * `entidad` solo hace falta donde el usuario esta escribiendo algo. Sin ella,
 * un fallo de validacion se cuenta como lo que es: un dato guardado que no
 * encaja.
 */
export function mensajeDeError(caught: unknown, entidad?: Entidad): string {
  const problemas = problemasDe(caught);

  if (problemas === null) {
    // No es validacion: un fallo de Dexie, de red o de cualquier otra cosa. Su
    // `message` es una frase de verdad y ayuda mas que ocultarla.
    return detalleDeError(caught);
  }

  if (entidad === undefined) return MENSAJE_DATO_GUARDADO;

  const frases: Record<string, string> = FRASES[entidad];

  // El primer campo que el usuario PUEDE arreglar, no el primero del error. Un
  // problema en una columna de sincronizacion llegando antes que el de la
  // altura mandaria a mirar donde no se puede hacer nada.
  for (const problema of problemas) {
    const campo = campoDe(problema);
    if (campo !== null && campo in frases) return frases[campo] as string;
  }

  return GENERICO_POR_ENTIDAD[entidad];
}
